import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

interface UseTabsListIndicatorProps {
  showIndicator: boolean;
  className?: string;
}

export const useTabsListIndicator = ({
  showIndicator,
  className,
}: UseTabsListIndicatorProps) => {
  const internalTabsListRef = useRef<HTMLDivElement | null>(null);
  const tabsListRef = useCallback((node: HTMLDivElement | null) => {
    internalTabsListRef.current = node;
  }, []);

  const [indicatorWidth, setIndicatorWidth] = useState<number>(0);
  const [indicatorPosition, setIndicatorPosition] = useState<number>(0);

  const onTabClick = (e: MouseEvent<HTMLButtonElement>) => {
    setIndicatorWidth(e.currentTarget.offsetWidth);
    setIndicatorPosition(e.currentTarget.offsetLeft);
  };

  const onPositionSet = (e: Event) => {
    const button = e.currentTarget as HTMLButtonElement;
    setIndicatorWidth(button.offsetWidth);
    setIndicatorPosition(button.offsetLeft);
  };

  useEffect(() => {
    if (!showIndicator) return;
    if (internalTabsListRef.current) {
      const activeTab = internalTabsListRef.current.querySelector(
        '[data-state="active"]'
      ) as HTMLElement | null;
      if (!activeTab) return;

      setIndicatorWidth(activeTab.offsetWidth);
      setIndicatorPosition(activeTab.offsetLeft);
    }
  }, [showIndicator, className]);

  useEffect(() => {
    if (!showIndicator) return;
    const tabs =
      internalTabsListRef.current?.querySelectorAll('button[role="tab"]');
    if (!tabs) return;

    tabs.forEach(tab => tab.addEventListener('click', onPositionSet));

    return () => {
      tabs.forEach(tab => tab.removeEventListener('click', onPositionSet));
    };
  }, [showIndicator]);

  return {
    onTabClick,
    tabsListRef,
    indicatorWidth,
    indicatorPosition,
  };
};
